import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Radio, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050B14] grain flex items-center justify-center px-5">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center gap-2 text-[#94A3B8] hover:text-[#0066FF] font-cond uppercase text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
      </div>
      <div className="relative w-full max-w-md rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-8 blue-box-glow" data-testid="login-card">
        <div className="flex items-center gap-2 justify-center mb-2">
          <Radio className="h-7 w-7 text-[#0066FF]" strokeWidth={2.5} />
        </div>
        <div className="text-center mb-8">
          <div className="font-display text-2xl tracking-wide">
            THE OTHER SIDE OF <span className="text-[#0066FF]">BLUE</span>
          </div>
          <div className="font-mono2 text-[10px] tracking-[0.3em] text-[#FF6B00] mt-1 flex items-center justify-center gap-1.5">
            <Lock className="h-3 w-3" /> Admin Access
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]"
            />
          </div>
          <div>
            <label className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3.5 py-3 text-sm outline-none focus:border-[#0066FF]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-3 font-cond font-bold uppercase tracking-wide text-white"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
